import moment from 'moment/moment';
import {debounce as _debounce, map as _map} from 'lodash';

import {showConstraintsModal} from '@modules/client/utils/constraintsTables';
import {convertToInt} from '@utils/helpers';
import {sortDropdownOptionsAlphanumerically} from '@utils/sorters';
import {showMessageBox} from '@utils/messageBox';
import {GetSportsForDropdown} from '@graphql/sport/query';
import {GetContentCategoriesForDropdown} from '@graphql/contentCategory/query';
import {GetTournamentCategoriesFilteredBySports} from '@graphql/tournamentCategory/query';
import {GetTournamentsByTournamentCategories} from '@graphql/tournament/query';
import {GetStageCategoriesForDropdown} from '@graphql/stageCategory/query';
import {contentCategoriesFilteredByLevelAndParents} from '@graphql/content';
import {EVENT_NON_SPORTRADAR_TYPE, EVENT_SPORTRADAR_TYPE, EVENT_STAGE_TYPE} from '@constants/variables';
import {gql} from 'react-apollo';
import {getSelectedValues} from '@appComponents/HOCFiltersWrapper';

import apolloClient from '../../../apolloClient';

const DATE_FORMAT = 'YYYY-MM-DD';

export const STAGE_CATEGORY_FILTERS_CONFIG = {
    level1: {
        name: 'firstStageCategories',
        loading: 'firstStageCategoriesLoading',
        fieldName: 'firstStageCategories',
        errorMessage: 'Failed to fetch first stage categories filter data',
    },
    level2: {
        name: 'secondStageCategories',
        loading: 'secondStageCategoriesLoading',
        fieldName: 'secondStageCategories',
        errorMessage: 'Failed to fetch second stage categories filter data',
    },
};

export function updateFilterState(prevState, {fromMaxDate, toMinDate}, setState) {
    const nextState = {...prevState};

    fromMaxDate = moment.isMoment(fromMaxDate) ? fromMaxDate : moment(fromMaxDate);
    toMinDate = moment.isMoment(toMinDate) ? toMinDate : moment(toMinDate);

    if (fromMaxDate && !moment(prevState.fromMaxDateDefault).isSame(fromMaxDate)) {
        nextState.fromMaxDateDefault = fromMaxDate;
        nextState.fromMaxDate = fromMaxDate;
    }

    if (toMinDate && !moment(prevState.toMinDateDefault).isSame(toMinDate)) {
        nextState.toMinDateDefault = toMinDate;
        nextState.toMinDate = toMinDate;
    }

    if (
        nextState.fromMaxDateDefault &&
        nextState.toMinDateDefault &&
        nextState.fromMaxDateDefault.format(`${DATE_FORMAT}`) === nextState.toMinDateDefault.format(`${DATE_FORMAT}`)
    ) {
        nextState.fromMaxTime = moment.isMoment(fromMaxDate) ? fromMaxDate : moment(fromMaxDate);
        nextState.toMinTime = moment.isMoment(toMinDate) ? toMinDate : moment(toMinDate);
    }

    setState(() => nextState);
}

export const onSubmit = (data) => {
    const from = moment(data.from).format(`${DATE_FORMAT} HH:mm`),
        to = moment(data.to).format(`${DATE_FORMAT} HH:mm`);

    if (from !== data.from && to !== data.to) {
        data.from = moment().format(`${DATE_FORMAT} 00:00`);
        data.to = null;
    } else if (from !== data.from) {
        data.from = moment(data.to).subtract(1, 'd').format(`${DATE_FORMAT} HH:mm`);
    } else if (to !== data.to) {
        data.to = null;
    }

    showConstraintsModal(data);

    return data;
};

export const getSports = async (setState, isStageEvent = false) => {
    try {
        const result = await apolloClient.query({
            query: GetSportsForDropdown,
            variables: {
                isStage: isStageEvent,
            },
        });

        const {sports} = result.data;
        const sportsData = sortDropdownOptionsAlphanumerically(sports.mapDataForDropdownWithIntVal());

        setState({
            sports: sportsData,
            sportsLoading: false,
        });
    } catch (error) {
        setState({
            sports: [],
            sportsLoading: false,
        });

        showMessageBox(
            'clientMessage',
            'Failed to fetch sports filter data',
            `${error}`,
            'error'
        );
    }
};

export const getTournamentCategories = async (
    propsForm,
    sportIds,
    selectedTournamentCategories,
    setState
) => {
    try {
        const result = await apolloClient.query({
            query: GetTournamentCategoriesFilteredBySports,
            variables: {
                sports: sportIds,
            },
        });

        const tournamentCategories = sortDropdownOptionsAlphanumerically(
            result.data.tournamentCategories.mapDataForDropdownWithIntVal()
        );

        setState({
            tournamentCategories: tournamentCategories,
            tournamentCategoriesLoading: false,
        });

        propsForm.changeValue(
            'tournamentCategories',
            getSelectedValues(selectedTournamentCategories, tournamentCategories)
        );
    } catch (error) {
        setState({
            tournamentCategories: [],
            tournamentCategoriesLoading: false,
        });

        showMessageBox(
            'clientMessage',
            'Failed to fetch tournament categories filter data',
            `${error}`,
            'error'
        );
    }
};

export const getTournaments = async (
    propsForm,
    tournamentCategoryIds,
    selectedTournaments,
    setState
) => {
    try {
        const result = await apolloClient.query({
            query: GetTournamentsByTournamentCategories,
            variables: {
                tournamentCategories: tournamentCategoryIds,
            },
        });

        const tournaments = sortDropdownOptionsAlphanumerically(
            result.data.tournaments.mapDataForDropdownWithIntVal()
        );

        setState({
            tournaments: tournaments,
            tournamentsLoading: false,
        });

        propsForm.changeValue(
            'tournaments',
            getSelectedValues(selectedTournaments, tournaments)
        );
    } catch (error) {
        setState({
            tournaments: [],
            tournamentsLoading: false,
        });

        showMessageBox(
            'clientMessage',
            'Failed to fetch tournaments filter data',
            `${error}`,
            'error'
        );
    }
};

export const getContentCategoriesLevel1 = (propsForm, setState) => {
    apolloClient.query({
        query: GetContentCategoriesForDropdown,
        variables: {
            level: 1,
        },
    }).then((result) => {
        const {contentCategories} = result.data;
        const contentCategoriesData = sortDropdownOptionsAlphanumerically(contentCategories.mapDataForDropdownWithIntVal());

        setState({
            contentCategoriesLevel1: contentCategoriesData,
            contentCategoriesLevel1Loading: false,
        });
    }).catch((error) => {
        setState({
            contentCategoriesLevel1: [],
            contentCategoriesLevel1Loading: false,
        });

        showMessageBox(
            'clientMessage',
            'Failed to fetch content categories filter data',
            `${error}`,
            'error'
        );
    });
};

export const getContentCategoriesLevel2 = (propsForm, contentCategoryLevel2Ids, selectedContentCategoriesLevel2, setState) => {
    apolloClient.query({
        query: gql(contentCategoriesFilteredByLevelAndParents),
        variables: {
            level: 2,
            parents: contentCategoryLevel2Ids,
        },
    }).then((result) => {
        const contentCategoriesLevel2 = sortDropdownOptionsAlphanumerically(
            result.data.contentCategories.mapDataForDropdownWithIntVal()
        );

        setState({
            contentCategoriesLevel2: contentCategoriesLevel2,
            contentCategoriesLevel2Loading: false,
        });

        propsForm.changeValue('contentCategoriesLevel2',
            getSelectedValues(selectedContentCategoriesLevel2, contentCategoriesLevel2)
        );

    }).catch((error) => {
        setState({
            contentCategoriesLevel2: [],
            contentCategoriesLevel2Loading: false,
        });

        showMessageBox(
            'clientMessage',
            'Failed to fetch 2nd level content categories filter data',
            `${error}`,
            'error'
        );
    });
};

export const getContentCategoriesLevel3 = (propsForm, contentCategoryLevel3Ids, selectedContentCategoriesLevel3, setState) => {
    apolloClient.query({
        query: gql(contentCategoriesFilteredByLevelAndParents),
        variables: {
            level: 3,
            parents: contentCategoryLevel3Ids,
        },
    }).then((result) => {
        const contentCategoriesLevel3 = sortDropdownOptionsAlphanumerically(
            result.data.contentCategories.mapDataForDropdownWithIntVal()
        );

        setState({
            contentCategoriesLevel3: contentCategoriesLevel3,
            contentCategoriesLevel3Loading: false,
        });

        propsForm.changeValue('contentCategoriesLevel3',
            getSelectedValues(selectedContentCategoriesLevel3, contentCategoriesLevel3)
        );

    }).catch((error) => {
        setState({
            contentCategoriesLevel3: [],
            contentCategoriesLevel3Loading: false,
        });

        showMessageBox(
            'clientMessage',
            'Failed to fetch 3rd level content categories filter data',
            `${error}`,
            'error'
        );
    });
};

const debouncedStateUpdate = (setStateFunc, stateUpdateObject) => {
    const debouncedUpdate = _debounce(() => {
        setStateFunc(stateUpdateObject);
    }, 1000);

    debouncedUpdate();
};

export const getMaxMinDate = (filters, restoreFilterValues) => {
    const {ClientBookings} = filters;
    const fromMaxDate = restoreFilterValues(ClientBookings, 'to');
    const toMinDate = restoreFilterValues(ClientBookings, 'from', moment());

    return {
        fromMaxDate,
        toMinDate,
    };
};

export const mapIdsToInt = (values) => (_map(values, convertToInt));

export const updateDateState = (prevState, date, name) => {
    const fromMaxDate = prevState.fromMaxDate || moment();
    const toMinDate = prevState.toMinDate || moment();

    return {
        fromMaxTime: null,
        toMinTime: null,
        ...('from' === name && {
            toMinDate: date,
            ...(date.format(DATE_FORMAT) === fromMaxDate.format(DATE_FORMAT) && {
                fromMaxTime: fromMaxDate,
                toMinTime: date,
            }),
        }),
        ...('to' === name && {
            fromMaxDate: date,
            ...(date.format(DATE_FORMAT) === toMinDate.format(DATE_FORMAT) && {
                fromMaxTime: date,
                toMinTime: toMinDate,
            }),
        }),
    };
};

export const handleOnEventTypeChange = (event, value) => {
    const type = value ? convertToInt(value) : null;
    const isStageEvent = (type === EVENT_STAGE_TYPE);
    const isSportRadarEvent = (type === EVENT_SPORTRADAR_TYPE);
    const showSportFilter = (isSportRadarEvent || isStageEvent);
    const hasContentCategoryLevel1 = (type === EVENT_NON_SPORTRADAR_TYPE);

    return {
        isStageEvent: isStageEvent,
        hasSelectedContentCategoryLevel1: false,
        hasSelectedSport: false,
        hasContentCategoryLevel1: hasContentCategoryLevel1,
        showSportFilter: showSportFilter,
        sports: [],
        sportsLoading: true,
    };
};

export const fetchContentCategoriesLevel1 = (
    selectedEventType,
    propsForm,
    setState
) => {
    const type = selectedEventType ? convertToInt(selectedEventType) : '';
    const isNonSportRadarEvent = (type === EVENT_NON_SPORTRADAR_TYPE);

    if (!isNonSportRadarEvent) {
        propsForm.changeValue('contentCategoriesLevel1', null);
        propsForm.changeValue('contentCategoriesLevel2', null);
        propsForm.changeValue('contentCategoriesLevel3', null);

        return;
    }

    getContentCategoriesLevel1(propsForm, setState);
};

export const handleOnSportChange = (event, value) => {
    const hasSelectedSport = 0 < value.length;

    let state = {
        hasSelectedSport,
        selectedSports: value,
        tournamentCategoriesLoading: hasSelectedSport,
        firstStageCategoriesLoading: hasSelectedSport,
    };

    if (!hasSelectedSport) {
        state.hasSelectedTournamentCategory = false;
        state.selectedTournamentCategories = [];
        state.tournamentCategories = [];
        state.tournaments = [];
        state.selectedTournaments = [];
        state.hasSelectedFirstStageCategory = false;
        state.selectedFirstStageCategories = [];
        state.firstStageCategories = [];
        state.secondStageCategories = [];
        state.selectedSecondStageCategories = [];
    }

    return state;
};

export const fetchSports = async (
    value,
    setState
) => {
    const type = value ? convertToInt(value) : null;
    const isStageEvent = (type === EVENT_STAGE_TYPE);
    const isSportRadarEvent = (type === EVENT_SPORTRADAR_TYPE);

    if (isStageEvent || isSportRadarEvent) {
        await getSports(setState, isStageEvent);
    }
};

export const fetchTournamentCategories = async (
    selectedSports,
    propsForm,
    selectedTournamentCategories,
    setState
) => {
    if (0 < selectedSports.length) {
        const sportIds = mapIdsToInt(selectedSports);

        await getTournamentCategories(
            propsForm,
            sportIds,
            selectedTournamentCategories,
            setState
        );
    } else {
        propsForm.changeValue('tournamentCategories', null);
        propsForm.changeValue('tournaments', null);
    }
};

export const handleOnTournamentCategoryChange = (event, value, setState, fetchTournaments) => {
    const hasSelectedTournamentCategory = 0 < value.length;

    const state = {
        hasSelectedTournamentCategory,
        selectedTournamentCategories: value,
        tournaments: [],
        tournamentsLoading: hasSelectedTournamentCategory,
    };

    if (!hasSelectedTournamentCategory) {
        state.tournaments = [];
        state.selectedTournaments = [];
    }

    setState(state, fetchTournaments);
};

export const fetchTournaments = async (
    selectedTournamentCategories,
    propsForm,
    selectedTournaments,
    setState
) => {
    if (0 < selectedTournamentCategories.length) {
        const tournamentCategoryIds = mapIdsToInt(selectedTournamentCategories);

        await getTournaments(
            propsForm,
            tournamentCategoryIds,
            selectedTournaments,
            setState
        );
    } else {
        propsForm.changeValue('tournaments', null);
    }
};

export const handleOnContentCategoryLevel1Change = (event, value, setState, fetchContentCategoryLevel2) => {
    const hasSelectedContentCategoryLevel1 = 0 < value.length;

    const state = {
        hasSelectedContentCategoryLevel1,
        contentCategoriesLevel2: [],
        contentCategoriesLevel2Loading: hasSelectedContentCategoryLevel1,
    };

    setState(state, fetchContentCategoryLevel2);
};

export const fetchContentCategoryLevel2 = (
    selectedContentCategory1,
    propsForm,
    selectedContentCategoriesLevel2,
    setState
) => {
    if (0 < selectedContentCategory1.length) {
        const contentCategoryLevel2Ids = mapIdsToInt(selectedContentCategory1);

        getContentCategoriesLevel2(
            propsForm,
            contentCategoryLevel2Ids,
            selectedContentCategoriesLevel2,
            setState
        );
    } else {
        propsForm.changeValue('contentCategoriesLevel2', null);
    }
};

export const handleOnContentCategoryLevel2Change = (event, value, setState, fetchContentCategoryLevel3) => {
    const hasSelectedContentCategoryLevel2 = 0 < value.length;

    const state = {
        hasSelectedContentCategoryLevel2,
        selectedContentCategoriesLevel2 : value,
        contentCategoriesLevel3: [],
        contentCategoriesLevel3Loading: hasSelectedContentCategoryLevel2,
    };

    setState(state, fetchContentCategoryLevel3);
};

export const fetchContentCategoryLevel3 = (
    selectedContentCategory2,
    propsForm,
    selectedContentCategoriesLevel3,
    setState
) => {
    if (0 < selectedContentCategory2.length) {
        const contentCategoryLevel3Ids = mapIdsToInt(selectedContentCategory2);

        getContentCategoriesLevel3(
            propsForm,
            contentCategoryLevel3Ids,
            selectedContentCategoriesLevel3,
            setState
        );
    } else {
        propsForm.changeValue('contentCategoriesLevel3', null);
    }
};

export const getStageCategories = async (
    level,
    variables,
    changeFieldValue,
    selectedValues,
    setState
) => {
    const config = STAGE_CATEGORY_FILTERS_CONFIG[level];

    try {
        const result = await apolloClient.query({
            query: GetStageCategoriesForDropdown,
            variables,
        });

        const stageCategories = sortDropdownOptionsAlphanumerically(
            result.data.stageCategories.mapDataForDropdownWithIntVal()
        );

        setState({
            [config.name]: stageCategories,
            [config.loading]: true,
        });

        debouncedStateUpdate(setState, {
            [config.name]: stageCategories,
            [config.loading]: false,
        });

        changeFieldValue(
            config.fieldName,
            getSelectedValues(selectedValues, stageCategories)
        );
    } catch (error) {
        setState({
            [config.name]: [],
            [config.loading]: false,
        });

        showMessageBox(
            'clientMessage',
            config.errorMessage,
            `${error}`,
            'error'
        );
    }
};

export const fetchFirstStageCategories = async (
    selectedSports,
    changeFieldValue,
    selectedFirstStageCategories,
    setState
) => {
    if (0 < selectedSports.length) {
        const sportIds = mapIdsToInt(selectedSports);

        await getStageCategories(
            'level1',
            {sports: sportIds, parents: null},
            changeFieldValue,
            selectedFirstStageCategories,
            setState
        );
    } else {
        changeFieldValue('firstStageCategories', null);
        changeFieldValue('secondStageCategories', null);
    }
};

export const handleOnFirstStageCategoryChange = (event, value) => {
    const hasSelectedFirstStageCategory = 0 < value.length;

    let state = {
        hasSelectedFirstStageCategory,
        selectedFirstStageCategories: value,
        secondStageCategories: [],
        secondStageCategoriesLoading: hasSelectedFirstStageCategory,
    };

    if (!hasSelectedFirstStageCategory) {
        state.secondStageCategories = [];
        state.selectedSecondStageCategories = [];
    }

    return state;
};

export const fetchSecondStageCategories = async (
    selectedFirstStageCategories,
    changeFieldValue,
    selectedSecondStageCategories,
    setState
) => {
    if (0 < selectedFirstStageCategories.length) {
        const parentIds = mapIdsToInt(selectedFirstStageCategories);

        await getStageCategories(
            'level2',
            {sports: null, parents: parentIds},
            changeFieldValue,
            selectedSecondStageCategories,
            setState
        );
    } else {
        changeFieldValue('secondStageCategories', null);
    }
};
